import React, { useState, useEffect } from 'react';
import { patientAPI } from '../../../lib/api';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Edit } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = ['full_name', 'date_of_birth', 'gender', 'phone', 'email', 'address',
  'emergency_contact', 'emergency_phone', 'blood_group', 'allergies', 'medical_history'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const fromPatient = (patient) => Object.fromEntries(FIELDS.map(f => [f, patient?.[f] || '']));

const Field = ({ label, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
  </div>
);

export const EditPatientDialog = ({ open, onOpenChange, patient, onSaved }) => {
  const [data, setData] = useState(() => fromPatient(patient));
  const set = (field) => (v) => setData(d => ({ ...d, [field]: v }));
  const onInput = (field) => (e) => set(field)(e.target.value);

  useEffect(() => {
    if (open) setData(fromPatient(patient));
  }, [open, patient]);

  const submit = async () => {
    const cleaned = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== '' && v != null));
    try {
      await patientAPI.update(patient.id, cleaned);
      toast.success('Patient details updated successfully');
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error('Failed to update patient details');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-patient-dialog">
        <DialogHeader><DialogTitle className="font-[Manrope]">Edit Patient Details</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name *"><Input value={data.full_name} onChange={onInput('full_name')} required /></Field>
            <Field label="Date of Birth"><Input type="date" value={data.date_of_birth} onChange={onInput('date_of_birth')} /></Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Gender">
              <Select value={data.gender} onValueChange={set('gender')}>
                <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone"><Input type="tel" value={data.phone} onChange={onInput('phone')} placeholder="+91 XXXXXXXXXX" /></Field>
          </div>

          <Field label="Email"><Input type="email" value={data.email} onChange={onInput('email')} placeholder="patient@example.com" /></Field>
          <Field label="Address"><Textarea value={data.address} onChange={onInput('address')} placeholder="Complete address" rows={3} /></Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Emergency Contact Name"><Input value={data.emergency_contact} onChange={onInput('emergency_contact')} /></Field>
            <Field label="Emergency Contact Phone"><Input type="tel" value={data.emergency_phone} onChange={onInput('emergency_phone')} /></Field>
          </div>

          <Field label="Blood Group">
            <Select value={data.blood_group} onValueChange={set('blood_group')}>
              <SelectTrigger><SelectValue placeholder="Select blood group" /></SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {BLOOD_GROUPS.map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Known Allergies"><Textarea value={data.allergies} onChange={onInput('allergies')} placeholder="List any known allergies" rows={2} /></Field>
          <Field label="Medical History"><Textarea value={data.medical_history} onChange={onInput('medical_history')} placeholder="Previous medical conditions, surgeries, etc." rows={3} /></Field>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={submit} data-testid="save-patient-btn">
              <Edit className="w-4 h-4 mr-2" />Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
